import { useState, useEffect, useCallback, useRef } from 'react'
import apiService, { SLOW_API_TIMEOUT_MS } from '@/services/api'

const dashboardStatsConfig = { timeout: SLOW_API_TIMEOUT_MS }
import { getAuthToken } from '@/utils/auth'

interface DashboardStats {
  activeShipments: number
  activeShipmentsTrend: number
  lowStockAlerts: number
  lowStockTrend: number
  stockTotalWeight?: number
  stockYtdWeight?: number
  stockYtdBags?: number
  stockYtdUniqueBl?: number
  stockCurrentWeight?: number
  stockCurrentBags?: number
  stockSeasonStart?: string
  stockTotalWeightTrend?: number
  pendingDocuments: number
  pendingDocumentsTrend: number
  containersDueForClearing: number
  pendingPayments: number
  pendingPaymentsTrend: number
  totalPaymentsAmount: number
  urgentContainers: number
}

interface Shipment {
  id: string
  containerNumber: string | null
  carrier: string | null
  pol: string | null
  pod: string | null
  etd: Date | null
  eta: Date | null
  status: string
  containerStatus: string
  booking: string | null
  vessel: string | null
  bl: string | null
  createdAt: Date
  updatedAt: Date
}

interface Document {
  id: string
  name: string
  type: string | null
  url: string
  size: number
  uploadedBy: string
  user: {
    id: string
    name: string
    email: string
  }
  uploadedAt: Date
  folder: string | null
  tags: string[]
}

interface InventoryLot {
  id: string
  lotNumber: string
  sku: string | null
  bags: number | null
  pieces: number | null
  balance: number | null
  location: string | null
  stockLevel: string
  weight: number | null
  updatedAt: Date
}

interface Notification {
  id: string
  userId: string
  type: string
  title: string
  message: string
  read: boolean
  link: string | null
  createdAt: Date
}

interface ContractCompletionData {
  chartData: Array<{
    month: string
    current: number
    prior: number
  }>
  currentTotal: number
  priorTotal: number
  currentTonnage?: number
  priorTonnage?: number
  percentageChange: number
  timeframe: string
}

export interface DeliveredTonnageData {
  seasonStart: string | null
  periodLabel: string | null
  asOfDate: string | null
  totalTonnes: number
  customerCount: number
  damagedMt?: number
  netSoundMt?: number
  targetMt?: number
  pctOfTarget?: number
  remainingToTarget?: number
  sourceNote?: string | null
  source?: string
  byCustomer: Array<{
    customer: string
    tonnes: number
    lots?: number
    share?: number | null
    notes?: string | null
  }>
  chartData: Array<{ customer: string; tonnes: number }>
}

interface ApiResponse<T> {
  success: boolean
  data: T
  message?: string
}

const DASHBOARD_STATS_CACHE_KEY = 'dashboard:stats:cache'

function readCache<T>(key: string): T | null {
  if (typeof window === 'undefined') return null
  const raw = sessionStorage.getItem(key)
  if (!raw) return null
  try {
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

function writeCache(key: string, value: unknown) {
  if (typeof window === 'undefined') return
  sessionStorage.setItem(key, JSON.stringify(value))
}

async function waitForAuthToken(maxAttempts = 20, delayMs = 150): Promise<string | null> {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const token = getAuthToken()
    if (token) return token
    await new Promise((resolve) => window.setTimeout(resolve, delayMs))
  }
  return getAuthToken()
}

const DASHBOARD_STATS_POLL_MS = 60_000

export const useDashboardStats = () => {
  const [stats, setStats] = useState<DashboardStats | null>(() => readCache(DASHBOARD_STATS_CACHE_KEY))
  const [loading, setLoading] = useState(() => readCache<DashboardStats>(DASHBOARD_STATS_CACHE_KEY) == null)
  const [error, setError] = useState<string | null>(null)
  const statsRef = useRef(stats)
  statsRef.current = stats

  const fetchStats = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true
    const token = await waitForAuthToken()
    if (!token) {
      setLoading(false)
      return
    }

    try {
      if (!silent && statsRef.current == null) setLoading(true)
      setError(null)
      const response = await apiService.get<ApiResponse<DashboardStats>>(
        '/dashboard/stats',
        dashboardStatsConfig
      )
      if (response.success) {
        setStats(response.data)
        writeCache(DASHBOARD_STATS_CACHE_KEY, response.data)
      } else {
        setError('Failed to fetch dashboard stats')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch dashboard stats')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    let inFlight = false

    const run = async (silent: boolean) => {
      if (cancelled || inFlight) return
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
      inFlight = true
      try {
        await fetchStats({ silent })
      } finally {
        inFlight = false
      }
    }

    void run(false)

    const intervalId = window.setInterval(() => {
      void run(true)
    }, DASHBOARD_STATS_POLL_MS)

    const onVisible = () => {
      if (document.visibilityState === 'visible') void run(true)
    }
    const onFocus = () => {
      void run(true)
    }

    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onFocus)

    return () => {
      cancelled = true
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onFocus)
    }
  }, [fetchStats])

  return { stats, loading, error, refetch: () => fetchStats({ silent: true }) }
}

export const useRecentShipments = (limit: number = 5, status?: string) => {
  const cacheKey = `dashboard:recent-shipments:${limit}:${status ?? 'all'}`
  const [shipments, setShipments] = useState<Shipment[]>(() => readCache<Shipment[]>(cacheKey) ?? [])
  const [loading, setLoading] = useState(() => readCache<Shipment[]>(cacheKey) == null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const fetchShipments = async () => {
      const token = await waitForAuthToken()
      if (cancelled) return
      if (!token) {
        setLoading(false)
        return
      }
      try {
        setLoading(readCache<Shipment[]>(cacheKey) == null)
        setError(null)
        const params = new URLSearchParams()
        params.append('limit', limit.toString())
        if (status) {
          params.append('status', status)
        }
        const response = await apiService.get<ApiResponse<{ shipments: Shipment[] }>>(
          `/dashboard/recent-shipments?${params.toString()}`
        )
        if (response.success) {
          setShipments(response.data.shipments)
          writeCache(cacheKey, response.data.shipments)
        } else {
          setError('Failed to fetch recent shipments')
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch recent shipments')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchShipments()
    return () => {
      cancelled = true
    }
  }, [cacheKey, limit, status])

  return { shipments, loading, error }
}

export const useRecentDocuments = (limit: number = 5) => {
  const cacheKey = `dashboard:recent-documents:${limit}`
  const [documents, setDocuments] = useState<Document[]>(() => readCache<Document[]>(cacheKey) ?? [])
  const [loading, setLoading] = useState(() => readCache<Document[]>(cacheKey) == null)
  const [error, setError] = useState<string | null>(null)

  const fetchDocuments = useCallback(async () => {
    const token = await waitForAuthToken()
    if (!token) {
      setLoading(false)
      return
    }
    try {
      setLoading(readCache<Document[]>(cacheKey) == null)
      setError(null)
      const response = await apiService.get<ApiResponse<{ documents: Document[] }>>(
        `/dashboard/recent-documents?limit=${limit}`
      )
      if (response.success) {
        setDocuments(response.data.documents)
        writeCache(cacheKey, response.data.documents)
      } else {
        setError('Failed to fetch recent documents')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch recent documents')
    } finally {
      setLoading(false)
    }
  }, [cacheKey, limit])

  useEffect(() => {
    fetchDocuments()
  }, [fetchDocuments])

  return { documents, loading, error, refetch: fetchDocuments }
}

export const useInventoryStatus = (limit: number = 5) => {
  const cacheKey = `dashboard:inventory-status:${limit}`
  const [lots, setLots] = useState<InventoryLot[]>(() => readCache<InventoryLot[]>(cacheKey) ?? [])
  const [loading, setLoading] = useState(() => readCache<InventoryLot[]>(cacheKey) == null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const fetchInventory = async () => {
      const token = await waitForAuthToken()
      if (cancelled) return
      if (!token) {
        setLoading(false)
        return
      }
      try {
        setLoading(readCache<InventoryLot[]>(cacheKey) == null)
        setError(null)
        const response = await apiService.get<ApiResponse<{ lots: InventoryLot[] }>>(
          `/dashboard/inventory-status?limit=${limit}`
        )
        if (response.success) {
          setLots(response.data.lots)
          writeCache(cacheKey, response.data.lots)
        } else {
          setError('Failed to fetch inventory status')
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch inventory status')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchInventory()
    return () => {
      cancelled = true
    }
  }, [cacheKey, limit])

  return { lots, loading, error }
}

export const useNotifications = (unreadOnly: boolean = false, page: number = 1, limit: number = 10) => {
  const cacheKey = `dashboard:notifications:${unreadOnly}:${page}:${limit}`
  const cached = readCache<{ notifications: Notification[]; total: number }>(cacheKey)
  const [notifications, setNotifications] = useState<Notification[]>(() => cached?.notifications ?? [])
  const [total, setTotal] = useState(() => cached?.total ?? 0)
  const [loading, setLoading] = useState(() => cached == null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const fetchNotifications = async () => {
      const token = await waitForAuthToken()
      if (cancelled) return
      if (!token) {
        setLoading(false)
        return
      }
      try {
        setLoading(readCache(cacheKey) == null)
        setError(null)
        const params = new URLSearchParams()
        params.append('page', page.toString())
        params.append('limit', limit.toString())
        if (unreadOnly) {
          params.append('unreadOnly', 'true')
        }
        const response = await apiService.get<ApiResponse<{ notifications: Notification[]; meta: any }>>(
          `/dashboard/notifications?${params.toString()}`
        )
        if (response.success) {
          setNotifications(response.data.notifications)
          setTotal(response.data.meta?.total || 0)
          writeCache(cacheKey, {
            notifications: response.data.notifications,
            total: response.data.meta?.total || 0,
          })
        } else {
          setError('Failed to fetch notifications')
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch notifications')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchNotifications()
    return () => {
      cancelled = true
    }
  }, [cacheKey, unreadOnly, page, limit])

  return { notifications, total, loading, error }
}

/** @deprecated Use useDeliveredTonnage — Contract Completion removed (Marc 30 Oct). */
export const useContractCompletion = (timeframe: 'week' | 'month' | 'year' = 'month') => {
  const cacheKey = `dashboard:contract-completion:v3:${timeframe}`
  const [data, setData] = useState<ContractCompletionData | null>(() => readCache(cacheKey))
  const [loading, setLoading] = useState(() => readCache<ContractCompletionData>(cacheKey) == null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const fetchData = async () => {
      const token = await waitForAuthToken()
      if (cancelled) return
      if (!token) {
        setLoading(false)
        return
      }
      try {
        setLoading(readCache(cacheKey) == null)
        setError(null)
        const response = await apiService.get<ApiResponse<ContractCompletionData>>(
          `/dashboard/contract-completion?timeframe=${timeframe}`
        )
        if (response.success) {
          setData(response.data)
          writeCache(cacheKey, response.data)
        } else {
          setError('Failed to fetch delivered tonnage data')
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch delivered tonnage data')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchData()
    return () => {
      cancelled = true
    }
  }, [cacheKey, timeframe])

  return { data, loading, error }
}

export const useDeliveredTonnage = () => {
  const cacheKey = 'dashboard:delivered-tonnage:v2'
  const [data, setData] = useState<DeliveredTonnageData | null>(() => readCache(cacheKey))
  const [loading, setLoading] = useState(() => readCache<DeliveredTonnageData>(cacheKey) == null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const fetchData = async () => {
      const token = await waitForAuthToken()
      if (cancelled) return
      if (!token) {
        setLoading(false)
        return
      }
      try {
        setLoading(readCache(cacheKey) == null)
        setError(null)
        const response = await apiService.get<ApiResponse<DeliveredTonnageData>>(
          '/dashboard/delivered-tonnage'
        )
        if (response.success) {
          setData(response.data)
          writeCache(cacheKey, response.data)
        } else {
          setError('Failed to fetch delivered tonnage')
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch delivered tonnage')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchData()
    return () => {
      cancelled = true
    }
  }, [cacheKey])

  return { data, loading, error }
}
